import React, { useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { useDataSets } from "../../../contexts/DataSetsContext";
import MetadataEditor from "../../../components/Metadata/MetadataEditor";
import { SchemaEditor } from "../../../components/Metadata/SchemaEditor";
import { summarizeMetadata } from "../../../utils/metadata";
import { describeSchema } from "../../../utils/datasetMetadata";
import { inferSchema } from "../../../utils/schemaInference";
import { useDatasetTypes } from "../../../hooks/useDatasetTypes";

// The dataset's metadata and its item-metadata schema. Both sections collapse, and the schema
// section keeps its export actions in the header so they are reachable even when collapsed.

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid var(--main-border-color);
  padding-top: 14px;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const HeaderToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 260px;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--main-font-color);
  text-align: left;
  cursor: pointer;
`;

const Title = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Summary = styled.span`
  font-size: 13px;
  color: #a0a6b1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: auto;
`;

const Action = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #a0a6b1;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  font-size: 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const Hint = styled.div`
  font-size: 13px;
  color: #a0a6b1;
  margin-top: -4px;
`;

export const MetadataDetails = ({ style = {}, onSelectTab = () => { }, onExportSchema = null }) => {
  const { updatedDataSet, setUpdatedDataSet } = useDataSets();

  const [showMetadata, setShowMetadata] = useState(true);
  const [showSchema, setShowSchema] = useState(true);
  const [copiedSchema, setCopiedSchema] = useState(false);
  const [copiedMetadata, setCopiedMetadata] = useState(false);

  const { byPublicId } = useDatasetTypes({ enabled: Boolean(updatedDataSet?.dataset_type_id) });

  if (!updatedDataSet) {
    return null;
  }

  const schema = updatedDataSet.metadata_schema ?? updatedDataSet.metadataSchema ?? null;

  const assignedType =
    updatedDataSet.dataset_type ?? byPublicId(updatedDataSet.dataset_type_id) ?? null;
  const typeSchema = assignedType?.metadata_schema ?? null;

  // The dataset may return the schema as an object or as JSON text; normalise before copying.
  const schemaJson = (() => {
    if (!schema) return "";

    if (typeof schema === "string") {
      try {
        return JSON.stringify(JSON.parse(schema), null, 2);
      } catch (error) {
        return schema;
      }
    }

    return JSON.stringify(schema, null, 2);
  })();

  // The dataset's own metadata, normalised to JSON text for copying.
  const metadataJson = (() => {
    const value = updatedDataSet.metadata;

    if (value === null || value === undefined) return "";

    if (typeof value === "string") {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (error) {
        return value;
      }
    }

    return JSON.stringify(value, null, 2);
  })();

  const copyText = async (text) => {
    if (!text) return false;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // Clipboard API unavailable (or denied): fall back to a temporary textarea.
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    return true;
  };

  const copySchema = async () => {
    if (!(await copyText(schemaJson))) return;

    setCopiedSchema(true);
    window.setTimeout(() => setCopiedSchema(false), 1600);
  };

  const copyMetadata = async () => {
    if (!(await copyText(metadataJson))) return;

    setCopiedMetadata(true);
    window.setTimeout(() => setCopiedMetadata(false), 1600);
  };

  const downloadSchema = () => {
    if (!schemaJson) return;

    const blob = new Blob([schemaJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const name = String(updatedDataSet?.name ?? "dataset").replace(/[^a-z0-9_-]+/gi, "-").toLowerCase();

    link.href = url;
    link.download = `${name}-metadata-schema.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", ...style }}>
      <Section>
        <HeaderRow>
          <HeaderToggle type="button" onClick={() => setShowMetadata((open) => !open)} aria-expanded={showMetadata}>
            <Icon icon={showMetadata ? "lucide:chevron-down" : "lucide:chevron-right"} />
            <Title>Metadata</Title>
            <Summary>{summarizeMetadata(updatedDataSet.metadata)}</Summary>
          </HeaderToggle>

          <ActionRow>
            {onExportSchema ? (
              <ActionButton
                type="button"
                onClick={onExportSchema}
                title="Export this data set's item metadata schema"
              >
                <Icon icon="lucide:braces" /> Export schema
              </ActionButton>
            ) : null}

            <ActionButton
              type="button"
              onClick={copyMetadata}
              disabled={!metadataJson}
              title="Copy this data set's metadata JSON"
            >
              <Icon icon={copiedMetadata ? "lucide:check" : "lucide:copy"} />
              {copiedMetadata ? "Copied" : "Copy JSON"}
            </ActionButton>

            <Action>{showMetadata ? "Collapse" : "Expand"}</Action>
          </ActionRow>
        </HeaderRow>

        {showMetadata ? (
          <>
            <Hint>
              Arbitrary JSON stored with this data set. Add, change or remove values here; they are
              saved with Save Changes.
            </Hint>

            <MetadataEditor
              value={updatedDataSet.metadata}
              onChange={(metadata) => {
                setUpdatedDataSet({ ...updatedDataSet, metadata });
              }}
            />
          </>
        ) : null}
      </Section>

      <Section>
        <HeaderRow>
          <HeaderToggle type="button" onClick={() => setShowSchema((open) => !open)} aria-expanded={showSchema}>
            <Icon icon={showSchema ? "lucide:chevron-down" : "lucide:chevron-right"} />
            <Title>Item metadata schema</Title>
            <Summary>{describeSchema(schema)}</Summary>
          </HeaderToggle>

          <ActionRow>
            <ActionButton
              type="button"
              onClick={copySchema}
              disabled={!schemaJson}
              title="Copy the schema JSON to the clipboard"
            >
              <Icon icon={copiedSchema ? "lucide:check" : "lucide:copy"} />
              {copiedSchema ? "Copied" : "Copy schema"}
            </ActionButton>

            <ActionButton type="button" onClick={downloadSchema} disabled={!schemaJson} title="Download the schema as JSON">
              <Icon icon="lucide:download" /> Download
            </ActionButton>

            {!schemaJson && metadataJson ? (
              <ActionButton
                type="button"
                onClick={() => {
                  try {
                    const value = JSON.parse(metadataJson);
                    setUpdatedDataSet({ ...updatedDataSet, metadata_schema: inferSchema(value) });
                  } catch (error) {
                    console.log("INFER SCHEMA ERROR: ", error);
                  }
                }}
                title="Derive a schema from this data set's metadata"
              >
                <Icon icon="lucide:wand-2" /> Infer from metadata
              </ActionButton>
            ) : null}

            {typeSchema ? (
              <ActionButton
                type="button"
                onClick={() => setUpdatedDataSet({ ...updatedDataSet, metadata_schema: typeSchema })}
                title="Restore the schema copied from this dataset's type"
              >
                <Icon icon="lucide:rotate-ccw" /> Reset to type default
              </ActionButton>
            ) : null}

            <Action>{showSchema ? "Collapse" : "Expand"}</Action>
          </ActionRow>
        </HeaderRow>

        {showSchema ? (
          <SchemaEditor
            hideLabel
            value={schema}
            onChange={(nextSchema) => {
              // `null` clears the schema; the backend accepts it on the dataset PUT.
              setUpdatedDataSet({ ...updatedDataSet, metadata_schema: nextSchema });
            }}
          />
        ) : null}
      </Section>


    </div>
  );
};

export default MetadataDetails;
